/** Howler.js wrapper for music, SFX, and audio layering */

import { Howl, Howler } from 'howler';
import { Logger } from '../utils/Logger';

const logger = new Logger('AudioManager');

export type MusicKey = string;
export type SFXKey = string;

/** Audio manifest mapping keys to file paths */
export const AUDIO_MANIFEST: Record<string, string> = {
  // Music
  'music_main': 'assets/audio/music/Basic_MainGameLoop.wav',
  'music_free': 'assets/audio/music/Basic_FreeGameLoop.wav',
  'music_jackpot': 'assets/audio/music/ZRF_JackpotPickLoop.wav',

  // SFX - Reel
  'sfx_spin_click': 'assets/audio/sfx/Basic_SpinClick.wav',
  'sfx_reel_stop_1': 'assets/audio/sfx/Basic_ReelStop1.wav',
  'sfx_reel_stop_2': 'assets/audio/sfx/Basic_ReelStop2.wav',
  'sfx_reel_stop_3': 'assets/audio/sfx/Basic_ReelStop3.wav',
  'sfx_reel_stop_4': 'assets/audio/sfx/Basic_ReelStop4.wav',
  'sfx_reel_stop_5': 'assets/audio/sfx/Basic_ReelStop5.wav',
  'sfx_reel_stop_6': 'assets/audio/sfx/Basic_ReelStop6.wav',

  // SFX - Scatter
  'sfx_scatter_hit_1': 'assets/audio/sfx/Basic_ScatterHit1.wav',
  'sfx_scatter_hit_2': 'assets/audio/sfx/Basic_ScatterHit2.wav',
  'sfx_scatter_hit_3': 'assets/audio/sfx/Basic_ScatterHit3.wav',
  'sfx_scatter_hit_4': 'assets/audio/sfx/Basic_ScatterHit4.wav',
  'sfx_scatter_hit_5': 'assets/audio/sfx/Basic_ScatterHit5.wav',

  // SFX - Wins
  'sfx_win_small': 'assets/audio/sfx/GXG_Generic_Win1a.wav',
  'sfx_win_single': 'assets/audio/sfx/GXG_Generic_Win_Single.wav',
  'sfx_win_big': 'assets/audio/sfx/GXG_Generic_BigWin.wav',
  'sfx_win_big_end': 'assets/audio/sfx/GXG_Generic_BigWin_END.wav',

  // SFX - Features
  'sfx_freespin_trigger': 'assets/audio/sfx/Basic_FreeSpinTrigger.wav',
  'sfx_freegame_end': 'assets/audio/sfx/Basic_FreeGameEnd.wav',

  // SFX - UI / Bet
  'sfx_bet_up': 'assets/audio/sfx/Basic_BetButtonUp.wav',
  'sfx_bet_down': 'assets/audio/sfx/Basic_BetButtonDown.wav',
  'sfx_bet_max': 'assets/audio/sfx/Basic_BetButtonMaxBet.wav',

  // SFX - Anticipation
  'sfx_anticipation': 'assets/audio/sfx/Basic_Anticipation.wav',

  // SFX - Rush countup 1-9
  'sfx_rush_countup_1': 'assets/audio/sfx/ZRF_RushCountup1.wav',
  'sfx_rush_countup_2': 'assets/audio/sfx/ZRF_RushCountup2.wav',
  'sfx_rush_countup_3': 'assets/audio/sfx/ZRF_RushCountup3.wav',
  'sfx_rush_countup_4': 'assets/audio/sfx/ZRF_RushCountup4.wav',
  'sfx_rush_countup_5': 'assets/audio/sfx/ZRF_RushCountup5.wav',
  'sfx_rush_countup_6': 'assets/audio/sfx/ZRF_RushCountup6.wav',
  'sfx_rush_countup_7': 'assets/audio/sfx/ZRF_RushCountup7.wav',
  'sfx_rush_countup_8': 'assets/audio/sfx/ZRF_RushCountup8.wav',
  'sfx_rush_countup_9': 'assets/audio/sfx/ZRF_RushCountup9.wav',

  // SFX - Rush banner 6-9
  'sfx_rush_banner_6': 'assets/audio/sfx/ZRF_RushBanner6.wav',
  'sfx_rush_banner_7': 'assets/audio/sfx/ZRF_RushBanner7.wav',
  'sfx_rush_banner_8': 'assets/audio/sfx/ZRF_RushBanner8.wav',
  'sfx_rush_banner_9': 'assets/audio/sfx/ZRF_RushBanner9.wav',

  // SFX - Orb reveal
  'sfx_orb_reveal': 'assets/audio/sfx/ZRF_OrbReveal.wav',
  'sfx_orb_reveal_enhanced': 'assets/audio/sfx/ZRF_OrbRevealEnhanced.wav',

  // SFX - Lightning
  'sfx_lightning_trigger': 'assets/audio/sfx/ZRF_LightningTrigger.wav',
  'sfx_lightning_to_zeus': 'assets/audio/sfx/ZRF_LightningToZeus.wav',

  // SFX - Rush / Jackpot
  'sfx_rush_payment': 'assets/audio/sfx/RushFeverPayment.wav',
  'sfx_jackpot_pick_enhanced': 'assets/audio/sfx/JackpotPickEnhanced.wav',
  'sfx_jackpots_doubled': 'assets/audio/sfx/JackpotsDoubled.wav',
  'sfx_jackpot_bell': 'assets/audio/sfx/ZRF_JackpotBell.wav',
};

/** Default crossfade duration in milliseconds */
const DEFAULT_CROSSFADE_MS = 1000;

/** Singleton audio manager wrapping Howler.js */
export class AudioManager {
  private static _instance: AudioManager | null = null;
  private _musicTrack: Howl | null = null;
  private _currentMusicKey: string | null = null;
  private _sfxPool: Map<string, Howl> = new Map();
  private _musicVolume: number = 0.5;
  private _sfxVolume: number = 0.8;
  private _isMuted: boolean = false;
  private _loaded: boolean = false;
  private _musicPaused: boolean = false;

  /** Timer handle for cleaning up the old track after a crossfade */
  private _crossfadeTimer: ReturnType<typeof setTimeout> | null = null;

  static getInstance(): AudioManager {
    if (!AudioManager._instance) {
      AudioManager._instance = new AudioManager();
    }
    return AudioManager._instance;
  }

  get isLoaded(): boolean {
    return this._loaded;
  }

  get isMuted(): boolean {
    return this._isMuted;
  }

  get currentMusicKey(): string | null {
    return this._currentMusicKey;
  }

  /** Unload all sounds and reset state so preloadAll() can be called again cleanly. */
  unloadAll(): void {
    logger.info('Unloading all audio assets...');

    // Cancel any in-progress crossfade cleanup timer
    if (this._crossfadeTimer !== null) {
      clearTimeout(this._crossfadeTimer);
      this._crossfadeTimer = null;
    }

    // Stop and unload every Howl in the pool
    for (const [key, howl] of this._sfxPool) {
      try {
        howl.stop();
        howl.unload();
      } catch (e) {
        logger.warn(`Error unloading ${key}: ${e}`);
      }
    }

    // Global Howler unload to release any leaked HTML5 Audio elements
    Howler.unload();

    this._sfxPool.clear();
    this._musicTrack = null;
    this._currentMusicKey = null;
    this._loaded = false;
    this._musicPaused = false;
    logger.info('All audio assets unloaded');
  }

  /** Preload all audio assets */
  async preloadAll(): Promise<void> {
    // Clean up any previously loaded sounds to avoid exhausting the HTML5 Audio pool
    if (this._sfxPool.size > 0) {
      this.unloadAll();
    }

    logger.info('Preloading audio assets...');
    const promises: Promise<void>[] = [];

    for (const [key, src] of Object.entries(AUDIO_MANIFEST)) {
      const isMusic = key.startsWith('music_');
      const promise = new Promise<void>((resolve) => {
        const howl = new Howl({
          src: [src],
          // Never use html5: true — it creates HTML5 Audio elements which have a
          // tiny pool (~6) that leaks across page loads and can never be reclaimed.
          // Web Audio API handles everything through a single AudioContext.
          html5: false,
          preload: true,
          volume: isMusic ? this._musicVolume : this._sfxVolume,
          loop: isMusic,
          onload: () => {
            logger.debug(`Loaded audio: ${key}`);
            resolve();
          },
          onloaderror: (_id: number, error: unknown) => {
            logger.warn(`Failed to load audio ${key}: ${error}`);
            resolve(); // Don't block on missing audio
          },
        });
        this._sfxPool.set(key, howl);
      });
      promises.push(promise);
    }

    await Promise.all(promises);
    this._loaded = true;
    logger.info(`Audio preload complete: ${this._sfxPool.size} sounds loaded`);
  }

  // ---------------------------------------------------------------------------
  // Music playback
  // ---------------------------------------------------------------------------

  /**
   * Start a music track (looping). If the same track is already playing this is
   * a no-op. If a different track is playing it will be stopped immediately
   * (for a smooth transition use `crossfadeTo` instead).
   */
  /** Track whether we've installed the global user-gesture resume listener */
  private _gestureListenerInstalled: boolean = false;

  /**
   * Install a persistent click/touch listener on the document that resumes
   * the AudioContext on every real user gesture. This is the only reliable way
   * to unlock audio across all browsers — PixiJS synthetic events don't count
   * as "user gestures" for autoplay policy purposes.
   */
  private _installGestureListener(): void {
    if (this._gestureListenerInstalled) return;
    const resumeOnGesture = () => {
      if (Howler.ctx && Howler.ctx.state === 'suspended') {
        Howler.ctx.resume().then(() => {
          logger.info('AudioContext resumed via user gesture');
        }).catch(() => {});
      }
    };
    // These fire on REAL user gestures (not PixiJS synthetic events)
    document.addEventListener('pointerdown', resumeOnGesture);
    document.addEventListener('touchstart', resumeOnGesture);
    document.addEventListener('keydown', resumeOnGesture);
    this._gestureListenerInstalled = true;
  }

  /** Resume the AudioContext if the browser suspended it (common on mobile / subsequent loads). */
  private _ensureAudioContext(): void {
    this._installGestureListener();
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      Howler.ctx.resume().catch(() => {});
    }
  }

  playMusic(key: MusicKey): void {
    this._ensureAudioContext();
    if (this._currentMusicKey === key && this._musicTrack && this._musicTrack.playing()) {
      return;
    }

    // Stop current track instantly
    if (this._musicTrack) {
      this._musicTrack.stop();
    }

    const howl = this._sfxPool.get(key);
    if (!howl) {
      logger.warn(`Music track not found: ${key}`);
      return;
    }

    howl.loop(true);
    howl.volume(this._isMuted ? 0 : this._musicVolume);
    howl.play();
    this._musicTrack = howl;
    this._currentMusicKey = key;
    this._musicPaused = false;
    logger.info(`Playing music: ${key}`);
  }

  /**
   * Crossfade from the current music track to a new one.
   * @param trackName  Key of the target music track in the manifest.
   * @param duration   Crossfade duration in ms (default 1000).
   */
  crossfadeTo(trackName: string, duration: number = DEFAULT_CROSSFADE_MS): void {
    if (this._currentMusicKey === trackName && this._musicTrack && this._musicTrack.playing()) {
      return; // already playing this track
    }

    // Cancel any in-progress crossfade cleanup timer
    if (this._crossfadeTimer !== null) {
      clearTimeout(this._crossfadeTimer);
      this._crossfadeTimer = null;
    }

    const newHowl = this._sfxPool.get(trackName);
    if (!newHowl) {
      logger.warn(`Crossfade target not found: ${trackName}`);
      return;
    }

    const oldTrack = this._musicTrack;
    const oldKey = this._currentMusicKey;

    // Fade out the old track (if any)
    if (oldTrack && oldTrack.playing()) {
      const currentVol = this._isMuted ? 0 : this._musicVolume;
      oldTrack.fade(currentVol, 0, duration);

      // Stop old track once fade completes
      this._crossfadeTimer = setTimeout(() => {
        oldTrack.stop();
        logger.debug(`Crossfade: stopped old track ${oldKey}`);
        this._crossfadeTimer = null;
      }, duration);
    }

    // Fade in the new track
    newHowl.loop(true);
    newHowl.volume(0);
    newHowl.play();
    newHowl.fade(0, this._isMuted ? 0 : this._musicVolume, duration);

    this._musicTrack = newHowl;
    this._currentMusicKey = trackName;
    this._musicPaused = false;
    logger.info(`Crossfading to: ${trackName} (${duration}ms)`);
  }

  /** Stop the current music track, optionally fading out first. */
  stopMusic(fadeOut: boolean = true, fadeOutMs: number = 500): void {
    if (!this._musicTrack) return;

    if (this._crossfadeTimer !== null) {
      clearTimeout(this._crossfadeTimer);
      this._crossfadeTimer = null;
    }

    if (fadeOut) {
      const vol = this._isMuted ? 0 : this._musicVolume;
      this._musicTrack.fade(vol, 0, fadeOutMs);
      const track = this._musicTrack;
      setTimeout(() => track.stop(), fadeOutMs);
    } else {
      this._musicTrack.stop();
    }

    this._musicTrack = null;
    this._currentMusicKey = null;
    this._musicPaused = false;
    logger.info('Music stopped');
  }

  /** Pause the current music track. */
  pauseMusic(): void {
    if (this._musicTrack && this._musicTrack.playing()) {
      this._musicTrack.pause();
      this._musicPaused = true;
      logger.debug('Music paused');
    }
  }

  /** Resume the current music track if it was paused. */
  resumeMusic(): void {
    if (this._musicTrack && this._musicPaused) {
      this._musicTrack.play();
      this._musicPaused = false;
      logger.debug('Music resumed');
    }
  }

  // ---------------------------------------------------------------------------
  // SFX playback
  // ---------------------------------------------------------------------------

  /** Play a sound effect (fire-and-forget) */
  playSFX(key: SFXKey): number | undefined {
    this._ensureAudioContext();
    if (this._isMuted) return undefined;
    const howl = this._sfxPool.get(key);
    if (!howl) {
      logger.warn(`SFX not found: ${key}`);
      return undefined;
    }
    return howl.play();
  }

  /** Play reel stop sound for a given reel index (0-based) */
  playReelStop(reelIndex: number): void {
    const key = `sfx_reel_stop_${reelIndex + 1}`;
    this.playSFX(key);
  }

  /** Play spin start click */
  playSpinStart(): void {
    this.playSFX('sfx_spin_click');
  }

  /** Stop a specific sound effect instance */
  stopSFX(key: SFXKey, id?: number): void {
    const howl = this._sfxPool.get(key);
    if (id !== undefined) {
      howl?.stop(id);
    } else {
      howl?.stop();
    }
  }

  // ---------------------------------------------------------------------------
  // Convenience helpers
  // ---------------------------------------------------------------------------

  /** Play scatter hit sound for the Nth scatter landed (1-based count) */
  playScatterHit(count: number): void {
    const clamped = Math.min(Math.max(count, 1), 5);
    const key = `sfx_scatter_hit_${clamped}`;
    this.playSFX(key);
    logger.debug(`Playing scatter hit: ${key}`);
  }

  /** Play anticipation tension loop; returns the sound ID for stopping later */
  private _anticipationId: number | undefined;

  playAnticipation(): void {
    const howl = this._sfxPool.get('sfx_anticipation');
    if (howl) {
      howl.loop(true);
      this._anticipationId = howl.play();
      logger.debug('Playing anticipation loop');
    } else {
      logger.warn('Anticipation sound not loaded — skipping');
    }
  }

  /** Stop the anticipation tension loop */
  stopAnticipation(): void {
    const howl = this._sfxPool.get('sfx_anticipation');
    if (howl && this._anticipationId !== undefined) {
      howl.stop(this._anticipationId);
      this._anticipationId = undefined;
      logger.debug('Stopped anticipation loop');
    }
  }

  /** Play rush countup sound (1-9) */
  playRushCountup(index: number): void {
    const clamped = Math.min(Math.max(index, 1), 9);
    this.playSFX(`sfx_rush_countup_${clamped}`);
  }

  /** Play rush banner sound (6-9) */
  playRushBanner(index: number): void {
    const clamped = Math.min(Math.max(index, 6), 9);
    this.playSFX(`sfx_rush_banner_${clamped}`);
  }

  /** Play free spin trigger sound */
  playFreeSpinTrigger(): void {
    this.playSFX('sfx_freespin_trigger');
  }

  /** Play free game end sound */
  playFreeGameEnd(): void {
    this.playSFX('sfx_freegame_end');
  }

  /** Play a win sound (small / single) */
  playWinSound(): void {
    this.playSFX('sfx_win_small');
  }

  /** Play big win sound */
  playBigWinSound(): void {
    this.playSFX('sfx_win_big');
  }

  /** Play big win end sting */
  playBigWinEnd(): void {
    this.playSFX('sfx_win_big_end');
  }

  // ---------------------------------------------------------------------------
  // Volume & mute controls
  // ---------------------------------------------------------------------------

  /** Set music volume (0-1) */
  setMusicVolume(v: number): void {
    this._musicVolume = v;
    if (this._musicTrack && !this._isMuted) {
      this._musicTrack.volume(v);
    }
  }

  /** Set SFX volume (0-1) */
  setSFXVolume(v: number): void {
    this._sfxVolume = v;
  }

  /** Toggle mute state */
  setMuted(muted: boolean): void {
    this._isMuted = muted;
    Howler.mute(muted);
    logger.info(`Audio ${muted ? 'muted' : 'unmuted'}`);
  }

  /** Mute all audio */
  mute(): void {
    this.setMuted(true);
  }

  /** Unmute all audio */
  unmute(): void {
    this.setMuted(false);
  }
}
